import { Suspense } from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router'
import { QueryClientProvider } from '@tanstack/react-query'
import { Spinner, Toaster } from '@/design-system'
import { queryClient } from './lib/queryClient'
import SignIn from './pages/AuthPages/SignIn'
import ResetPassword from './pages/AuthPages/ResetPassword'
import ResetPasswordConfirm from './pages/AuthPages/ResetPasswordConfirm'
import NotFound from './pages/OtherPage/NotFound'
import AppLayout from './layout/AppLayout'
import { ScrollToTop } from './components/common/ScrollToTop'
import { AuthProvider } from './context/auth'
import ProtectedRoute from './features/auth/ProtectedRoute'
import { AgentChat } from './pages/Agents'
import Profile from './pages/Profile/Profile'
import { ErrorBoundary } from './components/error/ErrorBoundary'
import { RouteErrorBoundary } from './components/error/RouteErrorBoundary'
import HomeDashboard from './pages/Home/HomeDashboard'
import SchedulerDashboard from './pages/Scheduler/SchedulerDashboard'
import HiddenSchedulerPanel from './pages/admin/HiddenSchedulerPanel'
import { lazyWithReload } from './lib/lazyWithReload'

// /kb arrasta o editor de markdown (CodeMirror do @uiw/react-md-editor): lazy
// mantém o chunk fora do bundle de quem nunca abre a Base de Conhecimento.
const KnowledgeBasePage = lazyWithReload(
  'KnowledgeBasePage',
  () => import('./pages/KnowledgeBase/KnowledgeBasePage')
)

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Toaster />
        <AuthProvider>
          <Router>
            <ScrollToTop />
            <RouteErrorBoundary>
              <Routes>
                {/* Protected Dashboard Layout */}
                <Route
                  element={
                    <ProtectedRoute>
                      <AppLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route
                    index
                    path="/"
                    element={<Navigate to="/agents/warranties/chat" replace />}
                  />
                  <Route path="/kanban" element={<HomeDashboard />} />
                  <Route path="/scheduler" element={<SchedulerDashboard />} />
                  <Route
                    path="/kb"
                    element={
                      <Suspense
                        fallback={
                          <div className="flex justify-center py-24">
                            <Spinner size="lg" />
                          </div>
                        }
                      >
                        <KnowledgeBasePage />
                      </Suspense>
                    }
                  />

                  {/* Agents */}
                  {/* <Route path="/agents" element={<AgentsList />} /> */}
                  {/* <Route path="/agents/:id" element={<AgentDetail />} /> */}
                  <Route path="/agents/:type/chat" element={<AgentChat />} />

                  {/* Profile */}
                  <Route path="/profile" element={<Profile />} />
                </Route>

                {/* Public Auth Routes */}
                <Route path="/signin" element={<SignIn />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route
                  path="/reset-password/confirm/:uid/:token"
                  element={<ResetPasswordConfirm />}
                />

                {/* Painel oculto — sem link em UI nenhuma. Acesso só por URL direta.
                  Independe de login porque a Lambda pode ser usada justamente
                  para religar o sistema quando o backend está fora do ar. */}
                <Route path="/_/scheduler" element={<HiddenSchedulerPanel />} />

                {/* Fallback Route */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </RouteErrorBoundary>
          </Router>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
